/**
 * Custom hook to process OAuth callback
 * Extracted from OAuthCallback component for better testability and separation of concerns
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { asyncStorage } from '../../../shared/services/storage/asyncStorageService';

const PROCESSING_DELAY_MS = 1500;
const ERROR_REDIRECT_DELAY_MS = 4000;
const SUCCESS_CHECK_DELAY_MS = 3000;

/**
 * Hook to process OAuth callback flow
 */
export const useProcessOAuthCallback = (provider, skipProcessing = false) => {
  const location = useLocation();
  const { handleOAuthCallback } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  /**
   * Process OAuth callback with error handling
   */
  const processCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const error_param = urlParams.get('error');
    const error_description = urlParams.get('error_description');
    let timeoutId = null;

    try {
      // Handle OAuth errors from provider
      if (error_param) {
        const errorMessage = error_description || `${provider} authentication failed`;
        setError(errorMessage);
        sessionStorage.setItem(`${provider}_error_message`, errorMessage);

        // Note: No cleanup needed - window.location.replace will unload page
        setTimeout(() => {
          window.location.replace('/');
        }, SUCCESS_CHECK_DELAY_MS);
        return;
      }

      // Validate authorization code
      if (!code) {
        const errorMessage = `No authorization code received from ${provider}`;
        setError(errorMessage);
        sessionStorage.setItem(`${provider}_error_message`, errorMessage);

        // Note: No cleanup needed - window.location.replace will unload page
        setTimeout(() => {
          window.location.replace('/');
        }, SUCCESS_CHECK_DELAY_MS);
        return;
      }

      // Prevent concurrent processing
      const processingKey = `processing${provider.charAt(0).toUpperCase() + provider.slice(1)}Callback`;
      if (window[processingKey]) {
        return;
      }
      window[processingKey] = true;

      try {
        // Add processing delay for better UX
        const processingDelay = new Promise(resolve => setTimeout(resolve, PROCESSING_DELAY_MS));

        // Process the OAuth callback
        const result = await handleOAuthCallback(code, provider);

        await processingDelay;

        if (result && result.success) {
          // Clear any failure flags on success
          sessionStorage.removeItem(`${provider}_auth_failed`);
          sessionStorage.removeItem(`${provider}_error_message`);
          // handleOAuthCallback already navigates to dashboard
          return;
        } else {
          // Handle authentication failure
          const errorMessage = result?.error || `${provider} authentication failed - please try again`;
          sessionStorage.setItem(`${provider}_auth_failed`, 'true');
          sessionStorage.setItem(`${provider}_error_message`, errorMessage);

          setError(errorMessage);
          setIsLoading(false);

          // Clear URL to prevent reprocessing
          window.history.replaceState({}, document.title, `/${provider}-callback-error`);

          // Note: No cleanup needed - window.location.replace will unload page
          setTimeout(() => {
            window.location.replace(`/?${provider}_failed=true`);
          }, ERROR_REDIRECT_DELAY_MS);
        }
      } catch (error) {
        let errorMessage = `${provider} authentication failed`;

        if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
        setIsLoading(false);

        // Set failure flags
        sessionStorage.setItem(`${provider}_auth_failed`, 'true');
        sessionStorage.setItem(`${provider}_error_message`, errorMessage);

        // Clear auth data
        sessionStorage.removeItem(`${provider}_auth_state`);
        sessionStorage.removeItem(`${provider}_auth_email`);
        asyncStorage.remove('userData');

        window.history.replaceState({}, document.title, `/${provider}-callback-error`);

        // Note: No cleanup needed - window.location.replace will unload page
        setTimeout(() => {
          window.location.replace(`/?${provider}_failed=true`);
        }, ERROR_REDIRECT_DELAY_MS);
      } finally {
        window[processingKey] = false;
      }
    } catch (err) {
      // Cleanup any pending timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      throw err;
    }
  }, [location, provider, handleOAuthCallback]);

  /**
   * Effect to handle OAuth callback on mount
   */
  useEffect(() => {
    // Check for previous failure flags
    const authFailed = sessionStorage.getItem(`${provider}_auth_failed`) === 'true';
    if (authFailed) {
      sessionStorage.removeItem(`${provider}_auth_failed`);
      window.location.replace('/');
      return;
    }

    // Process callback once
    if (!hasProcessed) {
      setHasProcessed(true);
      if (!skipProcessing) {
        processCallback();
      }
    }
  }, [hasProcessed, provider, processCallback, skipProcessing]);

  return {
    isLoading,
    error
  };
};

export default useProcessOAuthCallback;
